/**
 * Reusable Event Type Select Component
 * Uses react-select-async-paginate for efficient loading and pagination
 * Supports search, infinite scroll, and integrated quick add functionality
 * Styled to match PrimeReact Dropdown components
 */

import React, { useMemo, useState, useCallback } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Controller, useForm } from 'react-hook-form';
import { loadEventTypeOptions } from 'hooks/queries/useEventTypesPaginated';
import { createEventType } from 'services/Calender/calendarServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { fontAwesome } from 'constants/icon';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import '../shared/AsyncSelect.scss';

/**
 * EventTypeSelect Component
 *
 * @param {Object} props - Component props
 * @param {string|Object|null} props.value - Selected value (can be ID string, full option object, or null)
 * @param {Function} props.onChange - Change handler (value, actionMeta) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isClearable - Show clear button
 * @param {boolean} props.isDisabled - Disable the select
 * @param {boolean} props.isSearchable - Enable search (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} props.pageSize - Items per page (default: 20)
 * @param {string|array} props.practiceAreaId - Filter by practice area ID(s)
 * @param {Object} props.styles - Custom react-select styles
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input ID attribute
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {boolean} props.showQuickAdd - Show quick add button (default: false)
 * @param {Function} props.onEventTypeAdded - Callback after event type is added (optional)
 * @returns {JSX.Element}
 */
function EventTypeSelect({
  value = null,
  onChange,
  placeholder = 'Select event type',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  className = '',
  sort_order = 'alphabet_ascending',
  pageSize = 20,
  practiceAreaId = '',
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  showQuickAdd = false,
  onEventTypeAdded,
  ...rest
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  // Form for adding new event type
  const {
    control: addFormControl,
    formState: { errors: addFormErrors },
    handleSubmit,
    reset: resetAddForm,
  } = useForm();

  // Font Awesome icon options
  const iconOptions = useMemo(
    () =>
      Object.keys(fontAwesome).flatMap((category) =>
        fontAwesome[category].map((icon) => ({
          label: icon,
          value: icon,
        }))
      ),
    []
  );
  // Handle adding new event type
  const handleAddEventType = useCallback(
    async (data) => {
      setIsSubmitting(true);
      try {
        const response = await createEventType({ ...data, practice_area_id: 'all' });
        const newEventType = response?.data;
        const newEventTypeId = newEventType?.event_type_id;

        // Close the form
        setShowAddForm(false);
        resetAddForm();

        // Trigger refresh of options by updating key
        setRefreshKey((prev) => prev + 1);

        // Set the newly created event type as selected
        // Pass just the ID (the parent component expects the ID string)
        if (onChange && newEventTypeId) {
          onChange({ label: data?.event_type, value: newEventTypeId, icon: data?.event_type_icon });
        }

        // Call optional callback
        if (onEventTypeAdded) {
          onEventTypeAdded(newEventType);
        }

        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EVENT_TYPE_CREATE_SUCCESS);
      } catch (error) {
        console.error('Error creating event type:', error);
        addToast(false, toastConstant.toasterType.INFO, toastConstant.api.FAILED, toastConstant.message.EVENT_TYPE_CREATE_FAILURE);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onChange, onEventTypeAdded, addToast, resetAddForm]
  );

  // Handle cancel add form
  const handleCancelAdd = useCallback(() => {
    setShowAddForm(false);
    resetAddForm();
  }, [resetAddForm]);

  // Handle quick add button click
  const handleQuickAddClick = useCallback(() => {
    setShowAddForm(true);
  }, []);

  // Memoize the load options function with config
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadEventTypeOptions(search, loadedOptions, additional, {
        pageSize,
        sort_order,
        practiceAreaId,
      }),
    [pageSize, sort_order, practiceAreaId] // Reloads when practiceAreaId changes
  );

  // Custom option template with icon support
  const formatOptionLabel = ({ label, icon }) => (
    <div className="d-flex align-items-center">
      {icon && (
        <span className="mx-2">
          <i className={icon} />
        </span>
      )}
      {label}
    </div>
  );

  // Icon dropdown templates
  const iconValueTemplate = (option) => {
    return (
      <span className="mx-2">
        <i className={option.value} style={{ fontSize: '25px' }} />
      </span>
    );
  };

  const iconItemTemplate = (item, props) => {
    if (item) {
      return (
        <span className="mx-2">
          <i className={item.value} />
        </span>
      );
    }
    return <span>{props.placeholder}</span>;
  };

  // Debounce time for search input (in milliseconds)
  const debounceTimeout = 300;

  // Additional props for AsyncPaginate
  const additional = {
    nextToken: '',
  };

  // Merge custom styles with menu portal z-index
  const mergedStyles = useMemo(
    () => ({
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      ...styles,
    }),
    [styles]
  );

  // If showing add form, render the add form
  if (showAddForm) {
    return (
      <div className="d-flex w-100">
        <div className="d-flex gap-2 mb-2 w-100">
          <div>
            <Controller
              name="event_type_icon"
              control={addFormControl}
              rules={{ required: true }}
              defaultValue=""
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  name={field.name}
                  value={field.value}
                  panelClassName="add-event-icon-picker"
                  options={iconOptions}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Icon"
                  itemTemplate={iconValueTemplate}
                  valueTemplate={iconItemTemplate}
                  showClear={field.value ? true : false}
                  filter
                />
              )}
            />
            {addFormErrors?.event_type_icon && <InputErrorMessage>Select event icon</InputErrorMessage>}
          </div>
          <div className="w-100">
            <Controller
              name="event_type"
              control={addFormControl}
              rules={{ required: true }}
              defaultValue=""
              render={({ field }) => (
                <TextSnippetWrapper type="text" id={field.name} {...field} className="input-shadow w-100" placeholder="Event type name" />
              )}
            />
            {addFormErrors?.event_type && <InputErrorMessage>Enter Event type name</InputErrorMessage>}
          </div>
        </div>

        <div className="d-flex justify-content-end mt-1">
          <Button
            icon="pi pi-times"
            className="p-button-secondary outline ms-2"
            type="button"
            onClick={handleCancelAdd}
            disabled={isSubmitting}
          />
          <Button
            icon="pi pi-check"
            className="p-button-secondary ms-2"
            type="button"
            onClick={handleSubmit(handleAddEventType)}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
    );
  }

  // Otherwise, render the select with optional quick add button
  return (
    <div className="d-flex align-items-center w-100">
      <AsyncPaginate
        key={`${refreshKey}-${practiceAreaId}`} // Force refresh when key or practiceAreaId changes
        value={value}
        loadOptions={loadOptions}
        onChange={onChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        className={showQuickAdd ? 'flex-grow-1' : className}
        classNamePrefix="async-select-custom"
        styles={mergedStyles}
        additional={additional}
        debounceTimeout={debounceTimeout}
        formatOptionLabel={formatOptionLabel}
        cacheUniqs={[practiceAreaId]} // Clear cache when practiceAreaId changes
        name={name}
        id={id}
        onBlur={onBlur}
        onFocus={onFocus}
        loadingMessage={() => 'Loading types...'}
        noOptionsMessage={({ inputValue }) => (inputValue ? `No event types found for "${inputValue}"` : 'No event types available')}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(e) => e.target === document}
        {...rest}
      />
      {showQuickAdd && (
        <div className="col-sm-1 d-flex justify-content-end ps-2">
          <span className="pointer">
            <i className="fas fa-plus-circle event-type-quick-add" onClick={handleQuickAddClick}></i>
            <Tooltip content="Add Event Type" position="top" target=".event-type-quick-add" showDelay={500} />
          </span>
        </div>
      )}
    </div>
  );
}

export default EventTypeSelect;
